import { useEffect, useRef, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Camera, Home, ImageOff, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

const MANAGERS = ["owner", "admin", "supervisor"];

function useBlob(id) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u; setUrl(null);
    if (!id) { return undefined; }
    client.get(`/files/${id}/download`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => setUrl(false));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [id]);
  return url;
}

// Front-of-property photo so staff can recognise the building.
export function PropertyFrontPhoto({ propertyId, fileId, onChange, size = "lg", testid = "property-front-photo" }) {
  const { user } = useAuth();
  const canEdit = !!propertyId && MANAGERS.includes(user?.role);
  const url = useBlob(fileId);
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const box = size === "lg" ? "w-full sm:w-72 aspect-[4/3]" : "h-16 w-20";

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await client.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await client.put(`/properties/${propertyId}/front-photo`, { fileId: data.id });
      toast.success("Property photo saved"); onChange?.(data.id);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };

  const remove = async () => {
    if (!(await confirm({ title: "Remove photo", description: "Remove the front photo for this property?", confirmText: "Remove", destructive: true }))) return;
    try { await client.delete(`/properties/${propertyId}/front-photo`); toast.success("Photo removed"); onChange?.(null); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className={size === "lg" ? "space-y-2" : ""} data-testid={testid}>
      <div className={`${box} rounded-xl overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center`}>
        {url ? <a href={url} target="_blank" rel="noreferrer" className="block h-full w-full" data-testid={`${testid}-open`}><img src={url} alt="Front of property" className="h-full w-full object-cover" /></a>
          : fileId && url === null ? <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
          : fileId && url === false ? <div className="flex flex-col items-center text-slate-400 text-xs gap-1" data-testid={`${testid}-unavailable`}><ImageOff className="h-6 w-6" />Photo unavailable</div>
          : <div className="flex flex-col items-center text-slate-400 text-xs gap-1" data-testid={`${testid}-empty`}><Home className="h-6 w-6" />{size === "lg" && "No front photo yet"}</div>}
      </div>
      {canEdit && size === "lg" && (
        <div className="flex gap-2">
          <input ref={input} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`${testid}-input`} onChange={(e) => upload(e.target.files?.[0])} />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()} data-testid={`${testid}-upload`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Camera className="h-3.5 w-3.5 mr-1.5" />}{fileId ? "Replace photo" : "Add front photo"}
          </Button>
          {fileId && <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={remove} aria-label="Remove photo" title="Remove photo" data-testid={`${testid}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>
      )}
    </div>
  );
}
